package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.ConsumerDetails
import unifiedcontent.aggregator.Contact
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Text

/**
 * Editor composable for creating or editing a consumer. Displays a form with fields for all
 * consumer properties including ID, name, client ID, subscribed types, and contact info.
 *
 * When editing an existing consumer and the user clicks Submit, this editor checks if any
 * types were removed. If so, a confirmation dialog is shown requiring the user to type
 * "CONFIRM" before proceeding. This prevents accidental removal of types which could
 * disrupt content flow to downstream consumers.
 *
 * @param aggregator The aggregator service providing API access and navigation
 * @param consumer The existing consumer to edit, or null to create a new consumer
 */
@Composable
fun consumerEditor(aggregator: AggregatorService, consumer: ConsumerDetails? = null) {
    val scope = rememberCoroutineScope()
    var updatedConsumer by remember { mutableStateOf(consumer ?: ConsumerDetails("", "")) }

    // State for type removal confirmation dialog
    var showRemovalConfirmation by remember { mutableStateOf(false) }
    var removedTypeNames by remember { mutableStateOf(emptyList<String>()) }

    // Track the original types for comparison at submit time
    val originalTypes = remember { consumer?.types.orEmpty().toSet() }

    dialogView(if (consumer != null) "Edit Consumer" else "Create Consumer") {
        suspend fun save(): Boolean {
            return validate {
                assertField(updatedConsumer.id.length >= 5, "The id must be at least 5 characters")
                assertField(updatedConsumer.name.orEmpty().length >= 5, "The name must be at least 5 characters")
                orElse {
                    try {
                        if (consumer == null) {
                            aggregator.newConsumer(updatedConsumer)
                        } else {
                            aggregator.updateConsumer(updatedConsumer)
                        }
                    } catch (t: Throwable) {
                        errors.error("Unable to save consumer: ${t.message}")
                    }
                }
            }.success
        }

        content {
            dialogField(
                "ID",
                if (consumer == null) "Enter a unique id. The preferred format is `sxm/<workload-name>/<service-name>." else null
            ) {
                if (consumer == null) {
                    simpleTextField(updatedConsumer.id) {
                        updatedConsumer = updatedConsumer.copy(id = it ?: "")
                    }
                } else {
                    Text(consumer.id)
                }
            }

            dialogField("Name", "A human readable name of the consumer (i.e. Playback Services)") {
                simpleTextField(updatedConsumer.name) {
                    updatedConsumer = updatedConsumer.copy(name = it.orEmpty())
                }
            }

            dialogField("Client ID", "The Cognito client id, for those consumers that consume via API.") {
                simpleTextField(updatedConsumer.clientId) {
                    updatedConsumer = updatedConsumer.copy(clientId = it ?: "")
                }
            }

            dialogField("Slack Channel", "The name of the Slack channel for support.") {
                simpleTextField(updatedConsumer.slackChannel) {
                    updatedConsumer = updatedConsumer.copy(slackChannel = it ?: "")
                }
            }

            dialogField("Types", "This list of types that this consumer is interested in.") {
                scrollableCheckBoxSelector(
                    items = aggregator.entityTypes,
                    selectedItems = updatedConsumer.types.orEmpty().toSet(),
                    itemLabel = { it.name },
                    itemIdentifier = { it.type},
                    onSelectionChange = { newTypes ->
                        updatedConsumer = updatedConsumer.copy(
                            types = newTypes.toList()
                        )
                    }
                )
            }

            dialogField("AWS Accounts", "The AWS accounts of this service.") {
                simpleTextField(updatedConsumer.accounts?.joinToString(",")) {
                    updatedConsumer = updatedConsumer.copy(
                        accounts = it?.split(",")?.map { it.trim() }.orEmpty()
                    )
                }
            }

            dialogField("Contact Emails", "A comma separated list of emails of contacts for this consumer.") {
                simpleTextField(updatedConsumer.contact?.joinToString(",") { it.email }) {
                    updatedConsumer = updatedConsumer.copy(contact = it?.split(",")?.map { it.trim() }?.map {
                        Contact(it)
                    })
                }
            }

            dialogField("Service Registry ID", "The ID of this service in the Service Registry.") {
                simpleTextField(updatedConsumer.serviceRegistryId) {
                    updatedConsumer = updatedConsumer.copy(serviceRegistryId = it)
                }
            }

            dialogField("UCP Team", "The id or friendly id of the UCP team that owns this consumer. Members are managed in UCP.") {
                simpleTextField(updatedConsumer.teamId) {
                    updatedConsumer = updatedConsumer.copy(teamId = it)
                }
            }

            // Render the type removal confirmation dialog when triggered by the submit action
            if (showRemovalConfirmation) {
                TypeRemovalConfirmationDialog(
                    entityType = "consumer",
                    removedTypeNames = removedTypeNames,
                    onConfirm = { confirmed ->
                        if (confirmed) {
                            scope.launch {
                                if (save()) {
                                    aggregator.navigate("consumers/${encodeURIComponent(updatedConsumer.id)}")
                                }
                            }
                        }
                        showRemovalConfirmation = false
                    }
                )
            }
        }

        action {
            title = "Submit"
            primary = true
            showProgressOnAction = true
            this.action {
                val currentTypes = updatedConsumer.types.orEmpty().toSet()
                val removed = originalTypes - currentTypes

                // If types were removed, show removal confirmation first
                if (removed.isNotEmpty()) {
                    removedTypeNames = removed.map { typeId ->
                        aggregator.entityType(typeId)?.name ?: typeId
                    }
                    showRemovalConfirmation = true
                } else {
                    if (save()) {
                        aggregator.navigate("consumers/${encodeURIComponent(updatedConsumer.id)}")
                    }
                }
            }
        }
        action("Cancel") {
            aggregator.navigate(if (consumer != null) "consumers/${encodeURIComponent(consumer.id)}" else "consumers")
        }
    }
}
